import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, Plus, Copy, CheckCircle2, Trash2, Pencil, ListChecks } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api, fileUrl } from '../../lib/api';
import { ImageField } from './AdminCrud';

const PLATFORMS = { facebook: 'Facebook', newsroom: 'Newsroom', newsletter: 'Newsletter', website_alert: 'Website Alert' };
const STATUSES = { draft: 'Draft', approved: 'Approved', scheduled: 'Scheduled', posted: 'Posted' };
const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  approved: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
  scheduled: 'bg-blue-50 text-blue-800 border-blue-200',
  posted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

const EMPTY = { title: '', platform: 'facebook', status: 'draft', scheduled_date: '', scheduled_time: '', phase: '', notes: '', body: '', link: '', image_url: '' };

const todayStr = () => new Date().toISOString().slice(0, 10);
const weekEnd = () => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); };

export default function AdminScheduler() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState('all');
  const [editing, setEditing] = useState(null); // null | {..form}
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get('/admin/scheduler').then((r) => setItems(r.data || [])).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, []);

  // Support deep-link editing from the dashboard widget: /admin/scheduler?edit={id}
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && items.length && !editing) {
      const item = items.find((i) => i.id === editId);
      if (item) {
        setEditing({ ...EMPTY, ...item });
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams]);

  const filtered = useMemo(() => {
    const t = todayStr(); const w = weekEnd();
    return items.filter((i) => {
      if (view === 'today') return i.scheduled_date === t && i.status !== 'posted';
      if (view === 'week') return i.scheduled_date && i.scheduled_date >= t && i.scheduled_date <= w && i.status !== 'posted';
      if (view === 'scheduled') return i.status === 'scheduled';
      if (view === 'posted') return i.status === 'posted';
      return true;
    });
  }, [items, view]);

  const save = async () => {
    if (!editing.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (editing.id) {
        const { id, created_at, updated_at, posted_at, ...payload } = editing;
        await api.put(`/admin/scheduler/${id}`, payload);
      } else {
        await api.post('/admin/scheduler', editing);
      }
      toast.success('Saved');
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const markPosted = async (item) => {
    await api.put(`/admin/scheduler/${item.id}`, { status: 'posted' });
    toast.success('Marked as posted'); load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await api.delete(`/admin/scheduler/${item.id}`);
    toast.success('Deleted'); load();
  };

  const copyItem = (item) => {
    const text = [item.body, item.link].filter(Boolean).join('\n\n') || item.title;
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  const seed = async () => {
    const r = await api.post('/admin/scheduler/seed');
    toast.success(`Suggested launch sequence added (${r.data.created} new)`); load();
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div className="p-4 sm:p-6 max-w-5xl" data-testid="admin-scheduler-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-serif font-black text-[#1a2744] text-2xl flex items-center gap-2"><CalendarClock className="h-6 w-6 text-[#C9A84C]" /> Post Scheduler</h1>
          <p className="text-sm text-slate-500 mt-1">Planning tool only — nothing is auto-posted. Facebook posting stays manual unless an official API connection is approved by Steve.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seed} className="h-10 font-bold border-[#1a2744] text-[#1a2744]" data-testid="scheduler-seed-btn"><ListChecks className="h-4 w-4 mr-1.5" /> Add Launch Sequence</Button>
          <Button onClick={() => setEditing({ ...EMPTY })} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="scheduler-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New Entry</Button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="mt-5">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="scheduler-view-all">All</TabsTrigger>
          <TabsTrigger value="today" data-testid="scheduler-view-today">Today</TabsTrigger>
          <TabsTrigger value="week" data-testid="scheduler-view-week">This Week</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="scheduler-view-scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="posted" data-testid="scheduler-view-posted">Posted</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-4 space-y-3" data-testid="scheduler-list">
        {filtered.length === 0 && (
          <Card><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="scheduler-empty">Nothing here yet. Add the suggested launch sequence or create an entry.</CardContent></Card>
        )}
        {filtered.map((item) => (
          <Card key={item.id} data-testid={`scheduler-item-${item.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-[#1a2744]">{item.title}</p>
                    <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[item.status]}`}>{STATUSES[item.status]}</Badge>
                    <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{PLATFORMS[item.platform]}</Badge>
                    {item.phase && <Badge variant="outline" className="text-[10px] border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10 font-bold">{item.phase}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {item.scheduled_date ? `Scheduled ${item.scheduled_date}${item.scheduled_time ? ` at ${item.scheduled_time}` : ''}` : 'No date set'}
                    {item.link ? ` · ${item.link}` : ''}
                    {item.posted_at ? ` · posted ${item.posted_at.slice(0, 10)}` : ''}
                  </p>
                  {item.notes && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{item.notes}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => copyItem(item)} className="h-9 text-xs font-bold" data-testid={`scheduler-copy-${item.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                  {item.status !== 'posted' && (
                    <Button size="sm" onClick={() => markPosted(item)} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`scheduler-posted-${item.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Posted</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...EMPTY, ...item })} className="h-9 text-xs font-bold" data-testid={`scheduler-edit-${item.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(item)} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`scheduler-delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="scheduler-dialog">
          <DialogHeader><DialogTitle className="font-serif">{editing?.id ? 'Edit entry' : 'New scheduled post'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Draft title</label><Input value={editing.title} onChange={(e) => set('title', e.target.value)} className="h-10" data-testid="scheduler-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Platform</label>
                  <Select value={editing.platform} onValueChange={(v) => set('platform', v)}>
                    <SelectTrigger className="h-10" data-testid="scheduler-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PLATFORMS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
                  <Select value={editing.status} onValueChange={(v) => set('status', v)}>
                    <SelectTrigger className="h-10" data-testid="scheduler-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Scheduled date</label><Input type="date" value={editing.scheduled_date} onChange={(e) => set('scheduled_date', e.target.value)} className="h-10" data-testid="scheduler-date-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Scheduled time</label><Input type="time" value={editing.scheduled_time} onChange={(e) => set('scheduled_time', e.target.value)} className="h-10" data-testid="scheduler-time-input" /></div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Campaign phase</label><Input value={editing.phase} onChange={(e) => set('phase', e.target.value)} placeholder="e.g. Launch Day, Week 2" className="h-10" data-testid="scheduler-phase-input" /></div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Post copy (used by the Copy button)</label><Textarea value={editing.body} onChange={(e) => set('body', e.target.value)} rows={5} data-testid="scheduler-body-input" /></div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Related link</label><Input value={editing.link} onChange={(e) => set('link', e.target.value)} className="h-10" data-testid="scheduler-link-input" /></div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Related image</label><ImageField value={editing.image_url} onChange={(v) => set('image_url', v)} category="social" /></div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Notes</label><Textarea value={editing.notes} onChange={(e) => set('notes', e.target.value)} rows={2} data-testid="scheduler-notes-input" /></div>
              <Button onClick={save} disabled={busy} className="w-full h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="scheduler-save-btn">{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
